import { z } from 'zod';

/**
 * Financial Configuration Schema
 *
 * Defines financial parameters for optimization and analysis.
 */

export const FinancialSchema = z.object({
  // Escalation rates
  elec_cost_escalation_rate_fraction: z.number()
    .min(-0.1, 'Escalation rate must be >= -10%')
    .max(0.2, 'Escalation rate must be <= 20%')
    .default(0.03),

  // Discount rate for NPV calculations
  offtaker_discount_rate_fraction: z.number()
    .min(0, 'Discount rate must be >= 0')
    .max(0.3, 'Discount rate must be <= 30%')
    .default(0.08),

  // Analysis period (typically 1 year for hourly optimization)
  analysis_years: z.number()
    .int()
    .min(1)
    .max(30)
    .default(1),

  // Tax rate for tax benefits
  offtaker_tax_rate_fraction: z.number()
    .min(0)
    .max(0.5)
    .default(0.25),

  // O&M escalation
  om_cost_escalation_rate_fraction: z.number()
    .min(-0.1)
    .max(0.2)
    .default(0.02),
});

/**
 * Settings Configuration Schema
 *
 * Defines solver and scenario settings.
 */
export const SettingsSchema = z.object({
  // Location (for weather data lookup)
  zip_code: z.string()
    .regex(/^\d{5}(-\d{4})?$/, 'Invalid US zip code format')
    .optional(),

  // Time resolution
  // 1 = hourly (8760), 4 = 15-minute (35040)
  time_steps_per_hour: z.number()
    .int()
    .min(1)
    .max(4)
    .default(4), // Default to 15-min for accurate demand charges

  // Analysis year
  year: z.number()
    .int()
    .min(2020)
    .max(2050)
    .optional(),

  // Grid constraints (Vortx Flux specific)
  grid_import_limit_kw: z.number().min(0).optional(),
  grid_export_limit_kw: z.number().min(0).optional(),

  // Off-grid mode
  off_grid_flag: z.boolean().default(false),

  // BAU comparison
  run_bau: z.boolean().default(true),
});

export default { FinancialSchema, SettingsSchema };
